import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '@/utils/analytics';
import { getCurrentUTMParams } from '@/utils/utmTracking';

/**
 * Custom hook to automatically track page views when routes change
 * UTM parameters are automatically included when available
 * 
 * Usage:
 * Import this hook in your router component or App component
 * 
 * Example:
 * ```jsx
 * function App() {
 *   useAnalytics(); // Automatically tracks all route changes with UTM params
 *   return <Routes>...</Routes>
 * }
 * ```
 */
export const useAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Get current UTM parameters (from URL or localStorage)
    const utmParams = getCurrentUTMParams();
    
    // Track page view with UTM parameters included
    trackPageView(location.pathname + location.search, '', utmParams || {});
  }, [location]);
};

/**
 * Hook to track page views with custom titles
 * Useful when you want to provide more descriptive page names
 * UTM parameters are automatically included when available
 * 
 * @param {string} pageTitle - Custom title for the page
 * 
 * Example:
 * ```jsx
 * function DashboardPage() {
 *   usePageTracking('User Dashboard');
 *   return <div>Dashboard content</div>
 * }
 * ```
 */
export const usePageTracking = (pageTitle) => {
  const location = useLocation();

  useEffect(() => {
    // Get current UTM parameters (from URL or localStorage)
    const utmParams = getCurrentUTMParams();
    
    trackPageView(location.pathname + location.search, pageTitle, utmParams || {});
  }, [location, pageTitle]);
};

export default useAnalytics;

